import React, { useCallback } from 'react';
import VariantSelector from './variantSelector/variantSelector';
import { validateSelectedVariant, updateFinanceDataInTheDOM, getVariantSelectionInTheDOM } from './financeModalUtils';
import { addToCartHandler, updateMiniCart } from '../../../helpers/addToCartHandler';
import ajaxRequest from '../../../helpers/ajaxRequest';
import Modal from '../../../components/modal/rightSlideInModal';
import useV12FinanceData from '../../../components/v12FinanceCalculator/useV12FinanceData';
import V12FinanceCalculator from '../../../components/v12FinanceCalculator/v12FinanceCalculator';
import useHitachiFinanceData from '../../../components/hitachiFinanceCalculator/useHitachiFinanceData';
import HitachiFinanceCalculator from '../../../components/hitachiFinanceCalculator/hitachiFinanceCalculator';
import { MessagePropertiesContext } from '../../../contexts/messageProperties';
import {
  API,
  ProductVariant,
  FormattedMessageRenderer,
  HitachiFinanceData,
  V12FinanceData,
  PaymentType
} from '../../../types';

const { productCode, currentFinanceProvider } = window.globalProductDetails;

const variantsTypeLabel = document.getElementById('productVariantType')?.dataset.variantType;
const isHitachi = currentFinanceProvider.includes('hitachi') && !currentFinanceProvider.includes('v12');

function FinanceModalApp(): React.ReactElement {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [buttonIsLoading, setButtonIsLoading] = React.useState(false);
  const [buttonIsDisabled, setButtonIsDisabled] = React.useState(false);
  const [variantSelectorErrorMessage, setVariantSelectorErrorMessage] = React.useState('');
  const [selectedVariantCode, setSelectedVariantCode] = React.useState(productCode);
  const [variants, setVariants] = React.useState<ProductVariant[]>([]);
  const [variantsHasLoaded, setVariantsHasLoaded] = React.useState(false);
  const financeData: HitachiFinanceData | V12FinanceData = isHitachi
    ? useHitachiFinanceData({
        financeDetailsUrl: `${API.HITACHI_FINANCE_DETAILS_PDP}/${selectedVariantCode || productCode}`
      })
    : useV12FinanceData({
        financeDetailsUrl: `${API.V12_FINANCE_DETAILS_PDP}/${selectedVariantCode || productCode}`
      });

  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  const heading: string = formattedMessage({ id: 'product.finance.spreadTheCostModal', fallback: 'Spread The Cost' });
  const hasVariants = variants.length > 0;

  React.useEffect(() => {
    setSelectedVariantCode(getVariantSelectionInTheDOM());
  }, [isModalOpen]);

  React.useEffect(() => {
    ajaxRequest({ method: 'GET', url: API.PRODUCT_VARIANTS.replace('[productCode]', productCode) })
      .then(({ entries }) => {
        setVariants(entries);
        setVariantsHasLoaded(true);
      })
      .catch(() => {
        console.error('Unable to get product variants');
      });
  }, []);

  const handleAddToCart = useCallback((): void => {
    const errorMessage = validateSelectedVariant(variants, selectedVariantCode);
    if (errorMessage) {
      setVariantSelectorErrorMessage(errorMessage);
    } else {
      const formElement = document.querySelector('.add_to_cart_form') as HTMLFormElement;
      if (formElement) {
        setButtonIsLoading(true);
        updateFinanceDataInTheDOM(financeData);
        addToCartHandler(formElement)
          .then(updateMiniCart)
          .catch(() => {})
          .finally(() => {
            setButtonIsLoading(false);
            setIsModalOpen(false);
          });
      }
    }
  }, [financeData, variants, selectedVariantCode]);

  const AddToCartButton = useCallback(
    () => (
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={buttonIsLoading || buttonIsDisabled || !variantsHasLoaded}
        className="button--primary"
      >
        <span className={`loader ${buttonIsLoading ? '' : 'hide'}`} />
        <span className={`${buttonIsLoading ? 'hide' : ''}`}>
          {formattedMessage({ id: 'basket.add.to.basket.no.formatting', fallback: 'Add To Bag' })}
        </span>
      </button>
    ),
    [buttonIsLoading, buttonIsDisabled, variantsHasLoaded, handleAddToCart]
  );

  const handleClose = (): void => setIsModalOpen(false);
  const handleOpen = (): void => setIsModalOpen(true);

  return (
    <Modal
      onOpen={handleOpen}
      secondFooterButton={<AddToCartButton />}
      isOpen={isModalOpen}
      onClose={handleClose}
      heading={heading}
    >
      <div className="finance-modal">
        <div className="finance-modal__content">
          {financeData.financeType === PaymentType.V12 && (
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            <V12FinanceCalculator {...financeData} className="finance-calculator--v12-finance-modal" />
          )}
          {financeData.financeType === PaymentType.HITACHI && (
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            <HitachiFinanceCalculator {...financeData} className="finance-calculator--hitachi-finance-modal" />
          )}
          {hasVariants && (
            <>
              <hr className="finance-calculator__separator" />
              <VariantSelector
                variants={variants}
                className="finance-modal__variant-selector"
                setButtonIsDisabled={setButtonIsDisabled}
                selectedVariantCode={selectedVariantCode}
                setSelectedVariantCode={setSelectedVariantCode}
                variantSelectorErrorMessage={variantSelectorErrorMessage}
                setVariantSelectorErrorMessage={setVariantSelectorErrorMessage}
                variantsLabel={getVariantsLabel(formattedMessage, variantsTypeLabel)}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function getVariantsLabel(formattedMessage: FormattedMessageRenderer, variantsType = ''): string {
  const variantsTypeMessageMap = {
    RingSizeVariantProduct: {
      id: 'product.variants.select.RingSizeVariantProduct',
      fallback: 'Select a ring size'
    },
    BraceletLengthVariantProduct: {
      id: 'product.variants.select.BraceletLengthVariantProduct',
      fallback: 'Select a bracelet size'
    },
    NecklaceLengthVariantProduct: {
      id: 'product.variants.select.NecklaceLengthVariantProduct',
      fallback: 'Select a necklace length'
    },
    CaseSizeVariantProduct: {
      id: 'product.variants.select.CaseSizeVariantProduct',
      fallback: 'Select a case size'
    },
    EarringSizeVariantProduct: {
      id: 'product.variants.select.EarringSizeVariantProduct',
      fallback: 'Select an earring size'
    }
  };
  return formattedMessage(
    variantsTypeMessageMap[variantsType] || {
      id: 'product.variants.select.SelectASize',
      fallback: 'Select a size'
    }
  );
}

export default FinanceModalApp;
